import { getAppConfiguration } from "appConfig";
import { axiosEjFetch as axiosFetch } from "./axiosEjHelpers";

const endpoint = "/api/Station";

/**
 * Mirrors the gRPC device DeviceState enum used by the hardware client.
 * (UNKNOWN = 0, IDLE = 1, RECORD = 2)
 */
export enum StationDeviceState {
  Unknown = 0,
  Idle = 1,
  Record = 2,
}

export type RegisterStationResponse = {
  deviceId: string;
  deviceInternalId: string;
  providerId: string;
};

export type StationStatusPayload = {
  deviceId: string;
  deviceState: StationDeviceState;
  freeSpace: number;
  queuedFiles: number;
  wifiName?: string | null;
};

export async function registerStation(
  deviceId: string
): Promise<RegisterStationResponse> {
  const response = await axiosFetch.post(`${endpoint}/register`, { deviceId });
  return response.data as RegisterStationResponse;
}

export async function sendStationStatus(
  payload: StationStatusPayload
): Promise<void> {
  await axiosFetch.post(`${endpoint}/status`, payload);
}

/**
 * The teaser/welcome videos are returned as `video/mp4` binary streams from
 * anonymous endpoints, so the absolute URL can be used directly as a
 * `<video>` element source (no auth header required).
 */
export async function getStationMediaUrl(
  kind: "teaser" | "welcome",
  deviceId: string
): Promise<string> {
  const cfg = await getAppConfiguration();
  return `${cfg.apiUrl}${endpoint}/${kind}/${deviceId}`;
}

export type UploadStationPhotoParams = {
  deviceId: string;
  photoId: string;
  sessionId: string;
  file: Blob;
  isLowRes?: boolean;
  isDebug?: boolean;
};

export async function uploadStationPhoto({
  deviceId,
  photoId,
  sessionId,
  file,
  isLowRes = false,
  isDebug = false,
}: UploadStationPhotoParams): Promise<void> {
  const form = new FormData();
  form.append("deviceId", deviceId);
  form.append("photoId", photoId);
  form.append("sessionId", sessionId);
  form.append("isLowRes", String(isLowRes));
  form.append("isDebug", String(isDebug));
  form.append("file", file, `${photoId}.jpg`);
  await axiosFetch.post(`${endpoint}/photo`, form);
}

export type UploadStationVideoParams = {
  deviceId: string;
  videoId: string;
  sessionId: string;
  file: Blob;
  width: number;
  height: number;
  durationMs: number;
  thumbnail?: Blob | null;
};

export async function uploadStationVideo({
  deviceId,
  videoId,
  sessionId,
  file,
  width,
  height,
  durationMs,
  thumbnail,
}: UploadStationVideoParams): Promise<void> {
  const form = new FormData();
  form.append("deviceId", deviceId);
  form.append("videoId", videoId);
  form.append("sessionId", sessionId);
  form.append("width", String(width));
  form.append("height", String(height));
  form.append("durationMs", String(durationMs));
  form.append("file", file, `${videoId}.mp4`);
  if (thumbnail) {
    form.append("thumbnail", thumbnail, `${videoId}.jpg`);
  }
  await axiosFetch.post(`${endpoint}/video`, form);
}
